/**
 * Site Sidebar Navigation
 *
 * Renders the responsive global sidebar used across the SRAC site. It defines
 * public, member, officer, admin, and contest-director navigation sections,
 * applies feature-flag and role-based filtering, and shows message-count badges
 * for authenticated users.
 *
 * Copyright - see the LICENSE file in the repository root for terms.
 */

'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { SocialMediaLinks } from '@/components/features/social-media-links';
import { SidebarWeather } from '@/components/features/sidebar-weather';
import { cn } from '@/lib/utils';
import { useFirestore } from '@/firebase/index';
import { collection, query, where, onSnapshot, or, and } from 'firebase/firestore';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import {
  Menu,
  Home,
  Info,
  Calendar,
  Image,
  Video,
  Users,
  CreditCard,
  LogIn,
  UserCircle,
  Shield,
  LayoutDashboard,
  Settings,
  UserCog,
  Database,
  NotebookPen,
  Plane,
  FileText,
  Mail,
  Inbox,
  ShoppingCart,
  Flag,
  Timer,
  Medal,
  Sparkles,
  Trophy,
  Gauge,
  Award,
  DollarSign,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface NavSection {
  title: string;
  links: NavLink[];
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  requiresMember?: boolean;
  requiresOfficer?: boolean;
}

interface NavLink {
  href: string;
  label: string;
  icon: LucideIcon;
  /**
   * Optional feature-flag key — when present and the named flag is
   * false the link is hidden. Sections become invisible when all
   * their links are filtered out.
   */
  flag?: 'officerMessagesEnabled' | 'emailContactEnabled';
  /**
   * When set, the link is only visible to Contest Directors
   * (`isCD === true`). Lets us drop a CD-only entry into a public
   * section like Competitions without gating the whole section.
   */
  requiresCD?: boolean;
}

const navSections: NavSection[] = [
  {
    title: 'Main',
    links: [
      { href: '/', label: 'Home', icon: Home },
      { href: '/about', label: 'About', icon: Info },
      { href: '/posts', label: 'Posts', icon: FileText },
      { href: '/events', label: 'Events', icon: Calendar },
      { href: '/membership', label: 'Membership', icon: CreditCard },
      { href: '/flying', label: "What We're Flying", icon: Plane },
      { href: '/contact', label: 'Contact Us', icon: Mail },
    ],
  },
  {
    title: 'Gallery',
    links: [
      { href: '/gallery', label: 'Albums', icon: Image },
      { href: '/gallery/youtube-playlists', label: 'YouTube Playlists', icon: Video },
    ],
  },
  {
    title: 'Competitions',
    links: [
      { href: '/competitions', label: 'Overview', icon: Trophy },
      { href: '/competitions/pylon', label: 'Pylon', icon: Flag },
      { href: '/competitions/duration', label: 'Duration', icon: Timer },
      { href: '/competitions/decathlon', label: 'Decathlon', icon: Medal },
      { href: '/competitions/fun-fly', label: 'Fun Fly', icon: Sparkles },
      { href: '/competitions/practice', label: 'Lap Counter', icon: Gauge },
      { href: '/competitions/my', label: 'My Competitions', icon: UserCircle },
      {
        href: '/competitions/cd',
        label: 'Contest Director',
        icon: Award,
        requiresCD: true,
      },
    ],
  },
  {
    title: 'General',
    requiresAuth: true,
    links: [
      { href: '/field-logs', label: 'Field Logs', icon: NotebookPen },
    ],
  },
  {
    title: 'Members',
    requiresAuth: true,
    links: [
      { href: '/profile', label: 'My Profile', icon: UserCircle },
      { href: '/card', label: 'Membership Card', icon: CreditCard },
      { href: '/my-aircraft', label: 'What I Fly', icon: Plane },
      { href: '/members', label: 'Member Directory', icon: Users },
      { href: '/marketplace', label: 'Marketplace', icon: ShoppingCart },
      { href: '/messages', label: 'Messages', icon: Inbox },
    ],
  },
  {
    // First section — day-to-day content edits that admins/officers
    // touch most often. Site Content was previously labelled
    // "Membership Info" but its scope is broader (categories,
    // imagery, event types, social links, officer emails).
    title: 'Content Management',
    requiresAdmin: true,
    links: [
      { href: '/admin/posts', label: 'Posts', icon: FileText },
      { href: '/admin/membership-info', label: 'Site Content', icon: Database },
      { href: '/admin/competitions', label: 'Competitions', icon: Flag },
      { href: '/admin/events', label: 'Event Management', icon: Calendar },
    ],
  },
  {
    // Member-record + user-account management. /admin/members hosts
    // both the Memberships and Users sub-views via its own
    // contextual side nav.
    title: 'Membership Management',
    requiresAdmin: true,
    links: [
      { href: '/admin/members', label: 'Members', icon: Users },
      { href: '/admin/card-config', label: 'Card Configuration', icon: CreditCard },
    ],
  },
  {
    // Officer-only tools — currently just the shared contact-form
    // inbox at /admin/messages, but this is where new
    // officer-channel surfaces (treasurer reports, etc.) will land.
    title: 'Officers',
    requiresOfficer: true,
    links: [
      { href: '/admin/messages', label: 'Messages', icon: Mail, flag: 'officerMessagesEnabled' },
      { href: '/admin/payments', label: 'Payment Dashboard', icon: DollarSign },
    ],
  },
  {
    // Single entry; the /admin page renders its own contextual side
    // menu with Dashboard / Photo Sources / Video Sources under one
    // roof so admin utility pages don't crowd the global sidebar.
    title: 'Administration',
    requiresAdmin: true,
    links: [
      { href: '/admin', label: 'Administration', icon: LayoutDashboard },
    ],
  },
];

interface SidebarProps {
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  isMember?: boolean;
  isOfficer?: boolean;
  /** True when the user is a Contest Director for at least one race type. */
  isCD?: boolean;
  userId?: string;
  className?: string;
}

function SidebarContent({ isAuthenticated, isAdmin, isMember, isOfficer, isCD, userId }: SidebarProps) {
  const pathname = usePathname();
  const firestore = useFirestore();
  const { flags } = useFeatureFlags();
  const [memberMessageCount, setMemberMessageCount] = useState(0);
  const [adminMessageCount, setAdminMessageCount] = useState(0);

  // Check if user can view admin messages
  const canViewAdminMessages = isOfficer || isAdmin;

  // Query for member's personal messages
  const memberMessagesQuery = useMemo(() => {
    if (!userId) return null;
    return query(
      collection(firestore, 'messages'),
      where('receiverId', '==', userId),
      where('status', '==', 'new')
    );
  }, [firestore, userId]);

  // Query for admin messages (contact form submissions)
  const adminMessagesQuery = useMemo(() => {
    if (!canViewAdminMessages) return null;
    return query(
      collection(firestore, 'messages'),
      where('receiverId', '==', null),
      where('status', '==', 'new')
    );
  }, [firestore, canViewAdminMessages]);

  // Listen to member's personal messages
  useEffect(() => {
    if (!memberMessagesQuery) return;

    const unsubscribe = onSnapshot(
      memberMessagesQuery,
      (snapshot) => {
        setMemberMessageCount(snapshot.size);
      },
      (error) => {
        console.error('Error listening to member messages:', error);
      }
    );

    return () => unsubscribe();
  }, [memberMessagesQuery]);

  // Listen to admin messages
  useEffect(() => {
    if (!adminMessagesQuery) return;

    const unsubscribe = onSnapshot(
      adminMessagesQuery,
      (snapshot) => {
        setAdminMessageCount(snapshot.size);
      },
      (error) => {
        console.error('Error listening to admin messages:', error);
      }
    );

    return () => unsubscribe();
  }, [adminMessagesQuery]);

  // Filter sections in two passes: (1) gate sections by role, then
  // (2) gate individual links by feature flag and drop sections that
  // end up with zero visible links. Doing the link pass after the
  // role pass means a flag-only-link in a role-gated section
  // doesn't leak in for users who never had access to the section
  // in the first place.
  const filteredSections = navSections
    .filter((section) => {
      if (section.requiresAdmin && !isAdmin) return false;
      if (section.requiresOfficer && !(isOfficer || isAdmin)) return false;
      if (section.requiresMember && !isMember) return false;
      if (section.requiresAuth && !isAuthenticated) return false;
      return true;
    })
    .map((section) => ({
      ...section,
      links: section.links.filter((link) => {
        if (link.flag && !flags[link.flag]) return false;
        if (link.requiresCD && !isCD) return false;
        return true;
      }),
    }))
    .filter((section) => section.links.length > 0);
  const badgeCounts: Record<string, number> = {
    '/messages': memberMessageCount,
    '/admin/messages': adminMessageCount,
  };

  return (
    <ScrollArea className="h-full py-6 px-3">
      <div className="flex flex-col h-full">
        {/* Weather Widget */}
        <div className="mb-4 px-3">
          <SidebarWeather />
        </div>

        <div className="space-y-6 flex-1">
          {filteredSections.map((section, index) => (
            <div key={section.title}>
              {index > 0 && <Separator className="mb-4" />}
              <div className="px-3 py-2">
                <h2 className="mb-2 px-2 text-xs font-semibold tracking-tight text-muted-foreground uppercase">
                  {section.title}
                </h2>
                <div className="space-y-1">
                  {section.links.map((link) => {
                    const Icon = link.icon;
                    // Exact match or child route match
                    const isActive = pathname === link.href || pathname.startsWith(link.href + '/');

                    const badgeCount = badgeCounts[link.href] ?? 0;

                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="flex-1">{link.label}</span>
                        {badgeCount > 0 && (
                          <Badge
                            variant="destructive"
                            className="h-5 min-w-5 flex items-center justify-center px-1 text-xs"
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </Badge>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          {!isAuthenticated && (
            <>
              <Separator className="mb-4" />
              <div className="px-3">
                <Link
                  href="/login"
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </Link>
              </div>
            </>
          )}
        </div>
        
        {/* Social Media Links */}
        <div className="mt-auto pt-4 border-t">
          <div className="px-3 py-2">
            <h2 className="mb-3 px-2 text-xs font-semibold tracking-tight text-muted-foreground uppercase">
              Connect With Us
            </h2>
            <div className="px-2">
              <SocialMediaLinks className="flex items-center gap-4" />
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

export function Sidebar({ isAuthenticated, isAdmin, isMember, isOfficer, isCD, userId }: SidebarProps) {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle Navigation Menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-64 p-0 flex flex-col h-full">
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
        <div className="flex items-center p-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold">Navigation</h2>
        </div>
        <div className="flex-1 overflow-hidden" onClick={() => setOpen(false)}>
          <SidebarContent
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            isMember={isMember}
            isOfficer={isOfficer}
            isCD={isCD}
            userId={userId}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
